import * as LearningModuleActions from "src/app/store/learning-module/actions";
import * as UserActions from "src/app/store/user/actions";
import * as FromLearningModule from "src/app/store/learning-module/selectors";
import * as FromRouter from "src/app/store/router/selectors";
import { ChangeDetectionStrategy, Component } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { combineLatest, Observable } from "rxjs";
import { AppRoutes } from "src/app/constants";
import { RootState } from "src/app/store";
import { first, mergeMap, skipWhile } from "rxjs/operators";
import { ChapterDto, ChallengeDto, LearningModuleDto } from "@api";

@Component({
  selector: "app-challenges",
  templateUrl: "./challenges.page.html",
  styleUrls: ["./challenges.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChallengesPage {
  public readonly routes: typeof AppRoutes = AppRoutes;
  public readonly moduleId$: Observable<string> = this.store.select(FromRouter.selectModuleId);
  public readonly chapterId$: Observable<string> = this.store.select(FromRouter.selectChapterId);
  public readonly learningModule$: Observable<LearningModuleDto> = this.moduleId$.pipe(
    mergeMap(moduleId => this.store.select(FromLearningModule.selectLearningModule(moduleId))),
  );
  public readonly chapter$: Observable<ChapterDto> = this.chapterId$.pipe(
    mergeMap(chapterId => this.store.select(FromLearningModule.selectChapter(chapterId))),
  );
  public readonly challenges$: Observable<ChallengeDto[]> = this.chapterId$.pipe(
    mergeMap(chapterId => this.store.select(FromLearningModule.selectChapterChallenges(chapterId))),
  );
  public breadcrumbs: any[] = [];
  public defaultHref: string;

  constructor(private readonly store: Store<RootState>, private readonly i18n: TranslateService) {}

  protected ionViewWillEnter(): void {
    this.learningModule$
      .pipe(
        skipWhile(learningModule => !learningModule),
        first(),
      )
      .subscribe(learningModule => {
        this.breadcrumbs = [
          { name: this.i18n.instant("DASHBOARD.BREADCRUMB_NAME"), link: `/${this.routes.home}` },
          { name: this.i18n.instant("LEARNING_MODULES.BREADCRUMB_NAME"), link: `/${AppRoutes.learningModules}` },
          { name: learningModule.name, link: `/${AppRoutes.learningModules}/${learningModule.id}` },
        ];
        this.defaultHref = `/${AppRoutes.learningModules}/${learningModule.id}`;
      });
  }

  protected ionViewDidEnter(): void {
    combineLatest([this.moduleId$, this.chapterId$])
      .pipe(first())
      .subscribe(([learningModuleId, chapterId]) => {
        this.store.dispatch(LearningModuleActions.getLearningModule({ learningModuleId }));
        this.store.dispatch(LearningModuleActions.getChapter({ learningModuleId, chapterId }));
        this.store.dispatch(LearningModuleActions.getChapterChallenges({ learningModuleId, chapterId }));
        this.store.dispatch(UserActions.getCompletedSteps());
      });

    combineLatest([this.moduleId$, this.chapterId$, this.challenges$])
      .pipe(
        skipWhile(([, , challenges]) => !challenges.length),
        first(),
      )
      .subscribe(([learningModuleId, chapterId, chapters]) => {
        chapters.forEach(({ id }) => {
          this.store.dispatch(LearningModuleActions.getChallengeStepsCount({ learningModuleId, chapterId, challengeId: id }));
          this.store.dispatch(LearningModuleActions.getChallengeSteps({ learningModuleId, chapterId, challengeId: id }));
        });
      });
  }
}
